import { useQuery } from "@apollo/client";
import moment from "moment";
import { useMemo, useState } from "react";
import {
  ComplianceProgramActivityRowFragmentFragment,
  ComplianceProgramEventFragmentFragment,
  GetComplianceProgramActivityQuery,
  GetComplianceProgramActivityQueryVariables,
  GetComplianceProgramEventsQuery,
  GetComplianceProgramEventsQueryVariables,
} from "types/graphql-schema";

import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import Loading from "@components/loading/loading";
import { Select, SelectOption } from "@components/select/select";
import Table, {
  TableBody,
  TableBodyCell,
  TableBodyRow,
  TableContainer,
  TableHeadCell,
  TableHeadRow,
} from "@components/table/table";
import { TableSticky } from "@components/table/table-constants";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { assertEdgesNonNull } from "@helpers/helpers";

import getComplianceProgramActivityQuery from "../graphql/get-compliance-program-activity-query";
import getComplianceProgramEventsQuery from "../graphql/get-compliance-program-events-query";
import { getRecurredDates, getSkipReason } from "../helpers";
import { useProgramsSection } from "../programs-section-context";

/**
 * Nothing that happened before the activity log shipped was recorded. On a cycle created
 * before this date we say so, rather than showing an empty table that reads as "nothing
 * happened". Set this to the deploy date.
 */
export const ACTIVITY_HISTORY_STARTS_ON = "2026-09-08";

/**
 * Every verb the server can write, in the words the product uses. The verbs are named
 * after reviews, but this tab is also mounted under Surveys and Programs, so the noun
 * comes from the section rather than from the review label.
 */
const useEventLabels = (cycle: string) => {
  return useMemo<Record<string, string>>(
    () => ({
      "review.published": `${cycle} published`,
      "review.kickoff": `${cycle} kickoff`,
      "review.paused": `${cycle} paused`,
      "review.resumed": `${cycle} resumed`,
      "review.dates_changed": `${cycle} dates changed`,
      "review.recurred": `Next ${cycle.toLowerCase()} created`,
      "roster.synced": "Participants synced",
      "roster.participant_added": "Participant added",
      "roster.participant_removed": "Participant removed",
      "roster.participant_joined": `Joined the ${cycle.toLowerCase()}`,
      "notification.sent": "Notification sent",
      "reminder.created": "Reminder created",
      "reminder.updated": "Reminder updated",
      "reminder.deleted": "Reminder deleted",
      "reminder.sent": "Reminder sent",
      "assessment.reopened": "Assessment reopened",
    }),
    [cycle],
  );
};

const sourceLabels: Record<string, string> = {
  web: "web",
  slack: "Slack",
  teams: "Teams",
  sms: "SMS",
  mcp: "MCP client",
  cli: "command line",
  cron: "schedule",
  signal: "system",
  routine: "routine",
};

const channelLabels: Record<string, string> = {
  slack: "Slack",
  teams: "Teams",
  email: "email",
};

const statusLabels: Record<string, string> = {
  sent: "sent",
  failed: "failed",
  skipped: "skipped",
};

enum ActivityFilter {
  All = "All",
  Actions = "Actions",
  Notifications = "Notifications",
  Reminders = "Reminders",
}

/**
 * "Actions" is everything a person did to the cycle itself, so it is expressed as the
 * verb prefixes that are not a notification or a reminder.
 */
const verbPrefixesByFilter: Record<ActivityFilter, string[] | null> = {
  [ActivityFilter.All]: null,
  // eslint-disable-next-line local-rules/use-label -- these are server verb prefixes, not text a person reads
  [ActivityFilter.Actions]: ["review.", "roster.", "assessment."],
  [ActivityFilter.Notifications]: ["notification."],
  [ActivityFilter.Reminders]: ["reminder."],
};

const activityFilterOptions: SelectOption<ActivityFilter>[] = [
  { value: ActivityFilter.All, label: "All activity" },
  { value: ActivityFilter.Actions, label: "Actions" },
  { value: ActivityFilter.Notifications, label: "Notifications" },
  { value: ActivityFilter.Reminders, label: "Reminders" },
];

/** "138 sent, 3 failed, 4 skipped", leaving out the zeroes. */
const getBatchCounts = (row: ComplianceProgramActivityRowFragmentFragment) =>
  [
    row.sentCount > 0 ? `${row.sentCount} sent` : null,
    row.failedCount > 0 ? `${row.failedCount} failed` : null,
    row.skippedCount > 0 ? `${row.skippedCount} skipped` : null,
  ]
    .filter(Boolean)
    .join(", ");

/**
 * The people behind one send: who it reached, on what channel, and for a skip, why.
 * Fetched only when somebody opens the line, because a send can be 140 rows.
 */
const ComplianceProgramActivityPeopleRows = ({
  complianceProgramId,
  batch,
}: {
  complianceProgramId: number;
  batch: string;
}) => {
  const { data, loading, error, fetchMore } = useQuery<
    GetComplianceProgramEventsQuery,
    GetComplianceProgramEventsQueryVariables
  >(getComplianceProgramEventsQuery, {
    fetchPolicy: "cache-and-network",
    // Oldest first, so a person's Slack failure and the email after it read in that order.
    variables: { complianceProgramId, batch, orderBy: "created" },
  });

  const events: ComplianceProgramEventFragmentFragment[] = useMemo(
    () =>
      data?.complianceProgram?.events
        ? assertEdgesNonNull(data.complianceProgram.events)
        : [],
    [data],
  );
  const pageInfo = data?.complianceProgram?.events.pageInfo;

  // A send to a big roster is more rows than one page holds, and the people left out are
  // the ones an admin came looking for, so the rest have to be reachable.
  const handleLoadMorePeople = () => {
    if (!pageInfo?.endCursor) {
      return;
    }
    fetchMore({ variables: { after: pageInfo.endCursor, merge: true } });
  };

  if (events.length === 0 && loading) {
    return (
      <TableBodyRow>
        <TableBodyCell colSpan={5} className="pl-8">
          <Loading size="5" />
        </TableBodyCell>
      </TableBodyRow>
    );
  }

  if (events.length === 0 && error) {
    return (
      <TableBodyRow>
        <TableBodyCell colSpan={5} className="pl-8 text-gray-500">
          These people could not be loaded. Reload the page to try again.
        </TableBodyCell>
      </TableBodyRow>
    );
  }

  return (
    <>
      {events.map((event) => {
        const detail = [
          event.channel ? channelLabels[event.channel] : null,
          event.status ? statusLabels[event.status] : null,
          getSkipReason(event.payload),
          event.error || null,
        ].filter(Boolean);

        return (
          <TableBodyRow key={event.id}>
            <TableBodyCell className="pl-8 text-gray-500">
              {event.target?.name ?? "Unknown person"}
            </TableBodyCell>
            <TableBodyCell />
            <TableBodyCell className="text-gray-500">
              {moment(event.created).format("MMM D, YYYY h:mm a")}
            </TableBodyCell>
            <TableBodyCell />
            <TableBodyCell className="text-gray-500">
              {detail.join(" · ")}
            </TableBodyCell>
          </TableBodyRow>
        );
      })}
      {pageInfo?.hasNextPage && (
        <TableBodyRow>
          <TableBodyCell colSpan={5} className="pl-8">
            <Button
              theme={buttonTheme.text}
              text="Show more people"
              disabled={loading}
              onClick={handleLoadMorePeople}
            />
          </TableBodyCell>
        </TableBodyRow>
      )}
    </>
  );
};

const ComplianceProgramActivityRow = ({
  complianceProgramId,
  row,
  eventLabels,
}: {
  complianceProgramId: number;
  row: ComplianceProgramActivityRowFragmentFragment;
  eventLabels: Record<string, string>;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const verbLabel = eventLabels[row.verb] ?? row.verb;
  // A notification line is named after the message, which is what an admin is looking
  // for; the verb alone would say "Notification sent" on nine different things.
  const label = row.notificationLabel ?? verbLabel;
  const detail = row.isBatch
    ? getBatchCounts(row)
    : [
        row.target?.name,
        row.channel ? channelLabels[row.channel] : null,
        row.status ? statusLabels[row.status] : null,
        getSkipReason(row.payload),
        // A new cycle appearing on its own is only useful news if it says when it runs.
        row.verb === "review.recurred" ? getRecurredDates(row.payload) : null,
        row.error || null,
      ]
        .filter(Boolean)
        .join(" · ");

  return (
    <>
      <TableBodyRow>
        <TableBodyCell>
          {row.isBatch ? (
            <button
              type="button"
              className="flex items-center gap-1 text-left"
              aria-expanded={isExpanded}
              onClick={() => setIsExpanded(!isExpanded)}
            >
              <TopicflowIcon
                type={isExpanded ? "chevronDown" : "chevronRight"}
                className="h-4 w-4 shrink-0"
              />
              {label}
            </button>
          ) : (
            label
          )}
        </TableBodyCell>
        <TableBodyCell>{row.actor?.name ?? "Topicflow"}</TableBodyCell>
        <TableBodyCell>
          {moment(row.created).format("MMM D, YYYY h:mm a")}
        </TableBodyCell>
        <TableBodyCell>{sourceLabels[row.source] ?? row.source}</TableBodyCell>
        <TableBodyCell>{detail}</TableBodyCell>
      </TableBodyRow>
      {isExpanded && row.isBatch && (
        <ComplianceProgramActivityPeopleRows
          complianceProgramId={complianceProgramId}
          batch={row.batch}
        />
      )}
    </>
  );
};

const ComplianceProgramActivity = ({
  complianceProgramId,
}: {
  complianceProgramId: number;
}) => {
  const { noun } = useProgramsSection();
  const eventLabels = useEventLabels(noun);
  const [selectedFilter, setSelectedFilter] = useState<ActivityFilter>(
    ActivityFilter.All,
  );

  const { data, loading, error, fetchMore } = useQuery<
    GetComplianceProgramActivityQuery,
    GetComplianceProgramActivityQueryVariables
  >(getComplianceProgramActivityQuery, {
    fetchPolicy: "cache-and-network",
    variables: {
      complianceProgramId,
      verbPrefixIn: verbPrefixesByFilter[selectedFilter],
    },
  });

  const rows = useMemo(
    () =>
      data?.complianceProgram?.activity
        ? assertEdgesNonNull(data.complianceProgram.activity)
        : [],
    [data],
  );
  const pageInfo = data?.complianceProgram?.activity.pageInfo;
  const createdDatetime = data?.complianceProgram?.created;
  const startsBeforeTheLogExisted =
    !!createdDatetime &&
    moment(createdDatetime).isBefore(moment(ACTIVITY_HISTORY_STARTS_ON));

  const handleLoadMore = () => {
    if (!pageInfo?.endCursor) {
      return;
    }
    fetchMore({ variables: { after: pageInfo.endCursor, merge: true } });
  };

  return (
    <div className="flex flex-col gap-4" aria-label={`${noun} activity`}>
      {/* A select rather than a second row of tabs, which would sit right under the
          page tabs and read as one confusing stack. Room for more filters later. */}
      <div className="flex items-center gap-2">
        <Select<ActivityFilter>
          aria-label="Filter activity"
          value={selectedFilter}
          options={activityFilterOptions}
          onChange={(option) => setSelectedFilter(option.value)}
        />
      </div>

      {startsBeforeTheLogExisted && (
        <div className="text-sm text-gray-500">
          This {noun.toLowerCase()} started before activity was recorded.
          History starts on{" "}
          {moment(ACTIVITY_HISTORY_STARTS_ON).format("MMM D, YYYY")}.
        </div>
      )}

      {/* The log scrolls inside its own box with a stuck header, the same way the
          Progress tab's participant table does. */}
      <TableContainer scroll className="overscroll-x-contain max-h-[75dvh]">
        <Table className="w-full" sticky={TableSticky.onlyTop}>
          <TableHeadRow>
            <TableHeadCell roundedLeft widthPx={280}>
              Activity
            </TableHeadCell>
            <TableHeadCell widthPx={200}>Who</TableHeadCell>
            <TableHeadCell widthPx={200}>When</TableHeadCell>
            <TableHeadCell widthPx={140}>Through</TableHeadCell>
            <TableHeadCell>Details</TableHeadCell>
          </TableHeadRow>
          <TableBody>
            {rows.map((row) => (
              <ComplianceProgramActivityRow
                key={row.id}
                complianceProgramId={complianceProgramId}
                row={row}
                eventLabels={eventLabels}
              />
            ))}
            {rows.length === 0 && loading && (
              <TableBodyRow>
                <TableBodyCell colSpan={5} className="text-center p-4">
                  <Loading size="5" />
                </TableBodyCell>
              </TableBodyRow>
            )}
            {/* A failed request must not read as "nothing happened". This is a log:
                saying it is empty when we could not ask is the wrong lie to tell. */}
            {rows.length === 0 && !loading && error && (
              <TableBodyRow>
                <TableBodyCell
                  colSpan={5}
                  className="text-center p-4 text-gray-500"
                >
                  This activity could not be loaded. Reload the page to try
                  again.
                </TableBodyCell>
              </TableBodyRow>
            )}
            {rows.length === 0 && !loading && !error && (
              <TableBodyRow>
                <TableBodyCell
                  colSpan={5}
                  className="text-center p-4 text-gray-500"
                >
                  No activity yet.
                </TableBodyCell>
              </TableBodyRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {pageInfo?.hasNextPage && (
        <div>
          <Button
            theme={buttonTheme.lightBlue}
            text="Load more"
            disabled={loading}
            onClick={handleLoadMore}
          />
        </div>
      )}
    </div>
  );
};

export default ComplianceProgramActivity;
